import { lazy as reactLazy } from 'react'

// Recovery from a stale build after a deploy.
//
// A returning user can be served the previous build's index.html (by the CRA
// service worker's precache, or a cached page) whose JS/CSS chunks no longer
// exist on the server. The lazy route import then fails ("Loading chunk N
// failed") and the app renders a blank page. On such a failure we reload the
// page once so the browser fetches the current index.html. A timestamp in
// sessionStorage stops a reload loop if the chunk is really missing.

export const RELOAD_KEY = 'chunkReloadAt'
export const RELOAD_GUARD_MS = 30 * 1000

const CHUNK_ERROR = /Loading (CSS )?chunk [^\s]+ failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module/i

export const isChunkLoadError = error => {
  if (!error) return false
  if (error.name === 'ChunkLoadError' || error.code === 'CSS_CHUNK_LOAD_FAILED') return true
  return CHUNK_ERROR.test(String(error.message || error))
}

const readReloadAt = () => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0
  } catch (e) {
    return 0
  }
}

const writeReloadAt = time => {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(time))
    return true
  } catch (e) {
    return false
  }
}

// Reloads once for a chunk load error. Returns true when a reload was started.
// No reload when the last one was under RELOAD_GUARD_MS ago, or when
// sessionStorage is unavailable (we couldn't guard against a loop).
export const reloadForChunkError = (error, now = Date.now()) => {
  if (!isChunkLoadError(error)) return false
  if (now - readReloadAt() < RELOAD_GUARD_MS) return false
  if (!writeReloadAt(now)) return false
  window.location.reload()
  return true
}

// React.lazy that reloads the page instead of failing when the chunk is gone.
// While the reload is in flight the import stays pending, so Suspense keeps
// showing its fallback rather than an error.
export const lazy = factory =>
  reactLazy(() =>
    factory().catch(error => {
      if (reloadForChunkError(error)) return new Promise(() => {})
      throw error
    }),
  )

// Catches chunk failures outside React.lazy (other dynamic imports).
export const installChunkErrorReload = () => {
  window.addEventListener('unhandledrejection', event => {
    reloadForChunkError(event.reason)
  })
  window.addEventListener('error', event => {
    reloadForChunkError(event.error || event.message)
  })
}
